import { useState } from "react";
import { AppAction } from "@edlab-fms/shared";
import { useAuthQuery } from "../../lib/api/auth";
import { useBankAccountsQuery } from "../../lib/api/reference";
import { useOverviewTotalsQuery } from "../../lib/api/overview";
import { formatMoney } from "../../lib/format/money";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { LoadingSpinner } from "../../components/LoadingSpinner";
import { Notice } from "../../components/ui/Notice";
import { CreateTransferModal } from "../transactions/CreateTransferModal";

function formatBankKind(kind: string): string {
  return kind.charAt(0).toUpperCase() + kind.slice(1);
}

export function BankBalancesPanel() {
  const auth = useAuthQuery();
  const user = auth.data;
  const totals = useOverviewTotalsQuery(true);
  const banks = useBankAccountsQuery(true);
  const [transferOpen, setTransferOpen] = useState(false);

  const hasOrgScope = user?.permissions.unitScope === "all";
  const canTransfer = Boolean(user?.permissions.actions.includes(AppAction.TRANSFER_CREATE));

  if (totals.isLoading) {
    return <LoadingSpinner label="Loading bank balances…" />;
  }

  if (!hasOrgScope) {
    return (
      <Notice>
        Shared account balances are hidden from this view. Your assigned programme
        transactions remain available.
      </Notice>
    );
  }

  const accounts = totals.data?.overview.totals.bankAccounts ?? [];

  return (
    <div className="grid gap-6">
      <div className="grid gap-4 md:grid-cols-2">
        {accounts.map((account) => (
          <Card key={account.name} padding="md">
            <div className="flex items-center gap-2 text-edlab-muted">
              <span aria-hidden className="text-lg">
                🏦
              </span>
              <h3 className="font-semibold text-edlab-ink">{account.name}</h3>
            </div>
            <p className="mt-5 text-[27px] font-semibold tracking-tight text-edlab-ink">
              {formatMoney(account.balance)}
            </p>
            <p className="mt-1 text-xs text-edlab-muted">
              {formatBankKind(account.kind ?? "bank")} · Current posted balance
            </p>
          </Card>
        ))}
      </div>

      <div>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-semibold text-edlab-ink">Move money between accounts</h3>
          {canTransfer ? (
            <Button
              variant="secondary"
              onClick={() => {
                setTransferOpen(true);
              }}
            >
              Record transfer
            </Button>
          ) : null}
        </div>
        <Notice>
          An internal transfer changes account balances without adding income or expenditure. The
          live system will also include a manual reconciliation checklist.
        </Notice>
      </div>

      <CreateTransferModal
        open={transferOpen}
        onClose={() => {
          setTransferOpen(false);
        }}
        bankAccounts={banks.data?.bankAccounts ?? []}
      />
    </div>
  );
}
